<template>
  <div>
    <a-form
      class="mt-3"
      :form="form.fc"
      v-bind="formItemLayout"
      hideRequiredMark>
      <scope-radio :decorators="decorators" @change="scopeChange" :form="form" :label="$t('bill.budget_project')" />
      <a-form-item :label="$t('common.name')">
        <a-input v-decorator="decorators.name" :placeholder="$t('validator.resourceName')" />
        <name-repeated
            v-slot:extra
            res="budgets"
            version="v1"
            :name="form.fd.name" />
      </a-form-item>
      <a-form-item :label="$t('bill.budget_period_type')">
        <a-radio-group v-decorator="decorators.period_type">
          <a-radio-button v-for="item in PERIOD_TYPES" :value="item.key" :key="item.key">{{ item.value }}</a-radio-button>
        </a-radio-group>
      </a-form-item>
      <a-form-item :label="$t('bill.text_40')">
        <template v-if="mode === 'date'">
          <a-date-picker
            v-decorator="decorators.start_time"
            :disabled-date="disabledStartDate"
            format="YYYY-MM-DD" />
        </template>
        <template v-else-if="mode === 'month'">
          <a-month-picker
            v-decorator="decorators.start_time"
            :disabled-date="disabledStartDate"
            format="YYYY-MM" />
        </template>
        <template v-else-if="mode === 'year'">
          <a-date-picker
            :mode="mode"
            :open="startYear.open"
            @openChange="onStartYearOpenChange"
            @panelChange="onStartYearPanelChange"
            v-decorator="decorators.start_time"
            format="YYYY" />
        </template>
        <template v-else>
          <a-month-picker
            v-decorator="decorators.start_time"
            :disabled-date="disabledStartDate"
            format="YYYY-MM" />
        </template>
      </a-form-item>
      <a-form-item :label="$t('bill.text_41')">
        <template v-if="mode === 'date'">
          <a-date-picker
            v-decorator="decorators.end_time"
            :disabled-date="disabledEndDate"
            format="YYYY-MM-DD" />
        </template>
        <template v-else-if="mode === 'month'">
          <a-month-picker
            v-decorator="decorators.end_time"
            :disabled-date="disabledEndDate"
            format="YYYY-MM" />
        </template>
        <template v-else-if="mode === 'year'">
          <a-date-picker
            :mode="mode"
            :open="endYear.open"
            @openChange="onEndYearOpenChange"
            @panelChange="onEndYearPanelChange"
            v-decorator="decorators.end_time"
            format="YYYY" />
        </template>
        <template v-else>
          <a-month-picker
            v-decorator="decorators.end_time"
            :disabled-date="disabledEndDate"
            format="YYYY-MM" />
        </template>
      </a-form-item>
      <a-form-item :label="$t('bill.budget_currency')">
        <a-select v-decorator="decorators.currency">
          <a-select-option v-for="item in currencyOptions" :value="item.item_id" :key="item.item_id">{{ item.item_name }}</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item :label="$t('bill.budget_amount')">
        <a-input-number
          v-decorator="decorators.amount"
          :formatter="value => `${prefix}${value}`"
          :parser="value => value.replace(prefix, '')"
          :precision="2"
          :min="0"
          style="width: 360px" />
      </a-form-item>
      <advance-config-block>
          <a-form-item :label="$t('bill.text_232')">
            <base-select
              v-decorator="decorators.platform_brand"
              filterable
              id-key="item_id"
              name-key="item_name"
              minWidth="160px"
              :options="uniqueArr(options.platform_brands)"
              @change="v => change('platform_brand', v)"
              :select-props="{ placeholder: $t('bill.text_49') }" />
          </a-form-item>
          <a-form-item :label="$t('bill.text_60')">
            <base-select
              :selectProps="{labelInValue: true}"
              v-decorator="decorators.account_id"
              filterable
              id-key="item_id"
              name-key="item_name"
              minWidth="160px"
              @change="v => change('account_id', v)"
              :options="options.accounts"
              :select-props="{ placeholder: $t('bill.text_49') }" />
          </a-form-item>
          <a-form-item :label="$t('bill.text_233')">
            <base-select
              :selectProps="{labelInValue: true}"
              v-decorator="decorators.cloudprovider_id"
              filterable
              id-key="item_id"
              name-key="item_name"
              minWidth="160px"
              :options="options.cloudproviders"
              :select-props="{ placeholder: $t('bill.text_49') }" />
          </a-form-item>
          <a-form-item :label="$t('bill.text_83')">
            <base-select
              v-decorator="decorators.region_ext_id"
              :selectProps="{labelInValue: true}"
              filterable
              id-key="item_id"
              name-key="item_name"
              minWidth="160px"
              :options="options.regions"
              :select-props="{ placeholder: $t('bill.text_49') }" />
          </a-form-item>
          <a-form-item :label="$t('bill.text_73')">
            <base-select
              v-decorator="decorators.resource_type"
              filterable
              id-key="item_id"
              name-key="item_name"
              minWidth="160px"
              :options="options.resource_types"
              :labelFormat="resourceTypeFormat"
              @change="v => change('resource_type', v)"
              :select-props="{ placeholder: $t('bill.text_49') }" />
          </a-form-item>
          <a-form-item :label="$t('bill.text_79')" class="mb-0">
            <a-radio-group v-decorator="decorators.match_all" class="mb-2">
              <a-radio-button v-for="item in TAGS_MATCH_TYPES" :value="item.key" :key="item.key">{{ item.value }}</a-radio-button>
            </a-radio-group>
            <tag
              v-decorator="decorators.tag" extra="" :managerInstance="tagManager" :params="tagParams" />
          </a-form-item>
      </advance-config-block>
    </a-form>
  </div>
</template>

<script>
import * as R from 'ramda'
import { mapGetters, mapState } from 'vuex'
import _ from 'lodash'
import NameRepeated from '@/sections/NameRepeated/index'
import { PLATFORMS, ACCOUNTS, REGIONS, DOMAINS, PROJECTS, CHARGE_TYPES, CLOUDPROVIDERS, RESOURCES } from '@Bill/constants'
import ScopeRadio from '@/sections/ScopeRadio'
import { BRAND_MAP, CURRENCYS } from '@/constants'
import { uuid, accountsFilterByOem } from '@/utils/utils'
import validateForm from '@/utils/validate'
import { currencyUnitMap } from '@/constants/currency'
import Tag from '@/sections/Tag'
import { getBillResourceTypeLabel } from '@Bill/utils'
import { PERIOD_TYPES, TAGS_MATCH_TYPES } from '../../constants'

export default {
  components: {
    ScopeRadio,
    NameRepeated,
    Tag,
  },
  props: {
    collapseActive: {
      type: Array,
      default: () => {
        return []
      },
    },
  },
  data () {
    return {
      PERIOD_TYPES,
      CURRENCYS,
      TAGS_MATCH_TYPES,
      options: {
        resource_types: RESOURCES,
        platform_brands: PLATFORMS,
        accounts: ACCOUNTS,
        regions: REGIONS,
        projects: PROJECTS,
        chargeTypes: CHARGE_TYPES,
        domains: DOMAINS,
        cloudproviders: CLOUDPROVIDERS,
      },
      form: {
        fc: this.$form.createForm(this, {
          onValuesChange: (props, values) => {
            Object.keys(values).forEach((key) => {
              this.form.fd[key] = values[key]
            })
          },
        }),
        fd: {
          period_type: 'month',
          currency: '',
          start_time: '',
          end_time: '',
        },
      },
      decorators: {
        scope: [
          'scope',
          {
            initialValue: this.$store.getters.scope,
          },
        ],
        domain: [
          'domain',
          {
            rules: [
              { required: true, message: `${this.$t('common.select')}` },
            ],
          },
        ],
        project: [
          'project',
          {
            rules: [
              { required: true, message: `${this.$t('common.select')}` },
            ],
          },
        ],
        name: [
          'name',
          {
            rules: [
              { required: true, message: `${this.$t('common.placeholder')}${this.$t('common.name')}` },
              { validator: this.$validate('resourceName') },
            ],
          },
        ],
        period_type: [
          'period_type',
          {
            initialValue: 'month',
          },
        ],
        start_time: [
          'start_time',
          {
            rules: [
              { type: 'object', required: true, message: this.$t('common_653'), trigger: 'blur' },
            ],
          },
        ],
        end_time: [
          'end_time',
          {
            rules: [
              { type: 'object', required: true, message: this.$t('common_655'), trigger: 'blur' },
            ],
          },
        ],
        currency: [
          'currency',
          {
            rules: [
              { required: true, message: `${this.$t('common.tips.select', [this.$t('bill.budget_currency')])}` },
            ],
          },
        ],
        amount: [
          'amount',
          {
            rules: [
              { required: true, message: `${this.$t('common.placeholder')}${this.$t('bill.text_278')}` },
            ],
          },
        ],
        platform_brand: [
          'platform_brand',
          {
            initialValue: 'all',
          },
        ],
        account_id: [
          'account_id',
          {
            initialValue: { key: 'all' },
          },
        ],
        cloudprovider_id: [
          'cloudprovider_id',
          {
            initialValue: { key: 'all' },
          },
        ],
        region_ext_id: [
          'region_ext_id',
          {
            initialValue: { key: 'all' },
          },
        ],
        resource_type: [
          'resource_type',
          {
            initialValue: 'all',
          },
        ],
        match_all: [
          'match_all',
          {
            initialValue: false,
          },
        ],
        tag: [
          'tag',
          {
            rules: [
              { validator: validateForm('tagName') },
            ],
          },
        ],
      },
      formItemLayout: {
        wrapperCol: {
          span: 19,
        },
        labelCol: {
          span: 5,
        },
      },
      startYearOptions: [],
      endYearOptions: [],
      startYear: {
        open: false,
      },
      endYear: {
        open: false,
      },
      tagManager: new this.$Manager('res_tag_details', 'v1'),
    }
  },
  computed: {
    ...mapGetters(['isAdminMode', 'isDomainMode', 'scope']),
    ...mapState('common', {
      currency: state => state.bill.currency,
      currencyOpts: state => state.bill.currencyOpts,
    }),
    mode () {
      if (this.form.fd.period_type === 'day') return 'date'
      return this.form.fd.period_type
    },
    prefix () {
      return currencyUnitMap[this.form.fd.currency]?.sign || currencyUnitMap.CNY.sign
    },
    newCurrencys () {
      return this.CURRENCYS.filter(v => {
        return this.currencyOpts.find(obj => obj.item_id === v.key)
      })
    },
    currencyOptions () {
      return this.currencyOpts.map(val => {
        const localItem = currencyUnitMap[val.item_id]
        const text = localItem ? localItem.label : val.item_name
        return {
          ...val,
          item_name: val.item_id.indexOf('_') === -1 ? this.$t('bill.text_39', [text]) : this.$t('bill.text_287', [val.item_name.replace('_', '')]),
        }
      }).filter(item => {
        return item.item_id.indexOf('*') === -1
      })
    },
    tagParams () {
      const ret = {}
      const { start_time, end_time } = this.form.fd
      if (!start_time && !end_time) {
        ret.start_day = this.$moment().subtract(1, 'years').format('YYYYMMDD')
        ret.end_day = this.$moment().format('YYYYMMDD')
      } else {
        ret.start_day = start_time ? this.$moment(start_time).format('YYYYMMDD') : this.$moment(end_time).subtract(1, 'years').format('YYYYMMDD')
        ret.end_day = end_time ? this.$moment(end_time).format('YYYYMMDD') : this.$moment(start_time).add(1, 'years').format('YYYYMMDD')
      }
      return ret
    },
  },
  created () {
    // this.initCurrency()
    this.conditionM = new this.$Manager('bill_conditions', 'v1')
    this._conditionList('platform_brand', PLATFORMS)
    this._conditionList('resource_type', RESOURCES)
    if (this.isAdminMode) this._conditionList('domain', DOMAINS)
    if (this.isAdminMode || this.isDomainMode) this._conditionList('project', PROJECTS)
  },
  methods: {
    initCurrency () {
      this.form.fd.currency = this.currency
      this.$nextTick(() => {
        this.form.fc.setFieldsValue({
          currency: this.currency.replace('_', '').replace('*', ''),
        })
      })
    },
    change (type, value) {
      const id = R.type(value) === 'Object' ? value.key : value
      if (type === 'platform_brand') {
        this.$nextTick(() => {
          this.form.fc.setFieldsValue({
            account_id: { key: 'all' },
            cloudprovider_id: { key: 'all' },
            region_ext_id: { key: 'all' },
            resource_type: 'all',
          })
        })
        const query = { brand: id }
        this._conditionList('account', ACCOUNTS, query)
        this._conditionList('cloudprovider', CLOUDPROVIDERS, query)
        this._conditionList('region', REGIONS, query)
      }
      if (type === 'account_id') {
        this.$nextTick(() => {
          this.form.fc.setFieldsValue({
            cloudprovider_id: { key: 'all' },
            region_ext_id: { key: 'all' },
            resource_type: 'all',
          })
        })
        const query = { filter: `account_id.equals("${id}")` }
        this._conditionList('cloudprovider', CLOUDPROVIDERS, query)
        this._conditionList('region', REGIONS, query)
      }
    },
    uniqueArr (arr, byId = 'item_id') {
      const obj = {}
      arr.forEach(val => {
        obj[val[byId]] = val
      })
      return Object.values(obj)
    },
    _conditionList (queryType, cons = [], reqQuery = {}) {
      const adminParams = { admin: this.isAdminMode, scope: this.scope, $t: uuid() }
      const query = Object.assign({ query_type: queryType, cloudaccount_exist: true }, reqQuery, adminParams)
      this.conditionM.list({ params: query }).then(({ data: { data } }) => {
        if (queryType === 'platform_brand') {
          data = data.map(v => {
            return { ...v, item_name: (_.get(BRAND_MAP, `[${v.item_name}].label`) || v.item_name) }
          })
        }
        if (queryType === 'account') {
          data = accountsFilterByOem({ data, idkey: 'item_id', nameKey: 'item_name' })
        }
        this.options[`${queryType}s`] = cons.concat(data)
      })
    },
    domainChange (val) {
      this._conditionList('project', PROJECTS, { domain_id: val })
    },
    disabledStartDate (date) {
      const current = date.endOf('day')
      if (this.form.fd.end_time && current) {
        return current > this.form.fd.end_time.endOf('day')
      }
      return false
    },
    disabledEndDate (date) {
      const current = date.endOf('day')
      if (this.form.fd.start_time) {
        return current && current < this.form.fd.start_time.endOf('day')
      }
      return false
    },
    onStartYearOpenChange (v) {
      this.startYear.open = v
    },
    onStartYearPanelChange (v) {
      if (this.form.fd.end_time && v.year() > this.form.fd.end_time.year()) {
        this.$message.error(this.$t('bill.start_later_than_end', [this.form.fd.end_time.year()]))
        return
      }
      this.startYear.open = false
      this.$nextTick(() => {
        this.form.fc.setFieldsValue({ start_time: v })
      })
    },
    onEndYearOpenChange (v) {
      this.endYear.open = v
    },
    onEndYearPanelChange (v) {
      if (this.form.fd.start_time && v.year() < this.form.fd.start_time.year()) {
        this.$message.error(this.$t('bill.end_earlier_than_start', [this.form.fd.start_time.year()]))
        return
      }
      this.endYear.open = false
      this.$nextTick(() => {
        this.form.fc.setFieldsValue({ end_time: v })
      })
    },
    resourceTypeFormat (item) {
      if (item.item_id !== 'all') return getBillResourceTypeLabel(item.item_name)
    },
  },
}
</script>
